import getParentOrCanonicalRoute from '@jetshop/ui/Breadcrumbs/getParentsOrCanonicalRoute';
import { useProductVariants } from '@jetshop/core/hooks/useProductVariants';
import ProductPageLoadingState from './ProductPageLoadingState';
import { theme } from '../Theming/Theming';
import React, { useContext } from 'react';
import { styled } from 'linaria/react';
import { Above, Below } from '@jetshop/ui/Breakpoints';
import ProductDetails from './ProductDetails';
import ProductTabs from './ProductTabs/ProductTabs';
import Releware from './Releware';
import CustomData from './CustomData';
import { ContentPadding } from '../Layout/Content/Content';
import MaxWidth from '../Layout/MaxWidth';
import { useVariantFromUrl } from '@jetshop/core/hooks/useProductVariants/useVariantFromUrl';
import ProductRow from '../ui/ProductRow/ProductRow';
import ImageSlider from './ImageSlider/ImageSlider';
import { config } from '../../shop.config.custom';
import SiteContentContext from '../SiteContent/SiteContentContext';
import { CategoriesContent } from './CategoriesContent';
import { DesignerContent } from './DesignerContent';
import { KlaviyoViewedProduct } from '../Util/KlaviyoTracking';



const ProductPageContainer = styled(MaxWidth)`
  display: flex;
  align-items: center;
  flex-direction: column;
  ${theme.above.md} {
    padding-top: 1rem;
  }
  ${ContentPadding}
`;

const ProductContainer = styled('div')`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  justify-content: space-between;
  ${theme.below.lg} {
  }
`;

export const Section = styled('section')`
  width: 100%;
  ${theme.only.md} {
    padding-bottom: 1rem;
    &.product-images-wrapper {
      width: 45%;
    }
    &.product-info-wrapper {
      width: 50%;
    }
  }
  ${theme.above.lg} {
    padding-bottom: 2rem;
    &.product-images-wrapper {
      width: 48%;
    }
    &.product-info-wrapper {
      width: 48%;
    }
  }
  ${theme.below.sm} {
    &.product-images-wrapper {
      margin-left: -${theme.general.pagePadding.sm};
      width: 100vw;
      .main-slider {
        width: 100vw;
      }
    }
  }
`;

export const SectionHeading = styled('h2')`
  font-size: 1.2rem;
  margin-bottom: 1rem;
`;

const ProductTabsWrapper = styled('div')`
  margin-top: 1rem;
  width: 100%;
  ${theme.above.md} {
    //width: 50%;
  }
`;

const SmallProductRowWrapper = styled('div')`
  width: 100%;
  h2 {
    font-size: 1.2rem;
    padding-bottom: 1rem;
    padding-top: 2rem;
  }
`;

const Product = ({ state, result: { data, loading }, product }) => {
  //TRANSLATIONS
  const { bestSellingSubTitle, bestSellingTitle, blogTitle } =
    useContext(SiteContentContext);
  const initialVariant = useVariantFromUrl();
  //GET VARIANTS HANDLING
  const variantHandler = useProductVariants(product, {
    initialVariant,
    preselectCheapest: true,
  });

  // RETURN EARLY IF CACHE
  if (!product)
    return (
      <ProductPageContainer>
        <ProductContainer>
          <ProductPageLoadingState product={state && state.product} />
        </ProductContainer>
      </ProductPageContainer>
    );

  //VARIANTS VARIABLES
  const { selectedVariant: selectedVariation } = variantHandler;

  //CUSTOM FIX TO SHOW VARIANT IMAGE WHEN JUST COLOR IS SELECTED
  const variantImageSlider =
    selectedVariation ||
    (product?.variants?.options?.length > 1 &&
      variantHandler.missingOptions?.length === 1 &&
      variantHandler.getVariantForSelection(
        variantHandler.missingOptions[0].values[0],
        variantHandler.missingOptions[0]
      ));

  const parents = getParentOrCanonicalRoute(data?.route?.parents, product);
  const breadcrumbProps = {
    breadcrumbs: data?.route?.breadcrumbs,
    parents,
  };

  //PARENT ID
  const path = data?.route?.path;
  let parentCategory = false;

  if (product.categories && product.categories.length > 0) {
    parentCategory = product.categories[0];

    if (path.search(config.routes.blog.path) === -1) {
      const parentCartegories = product.categories.filter(
        (cat) => path.search(cat.primaryRoute.path) !== -1
      );
      parentCategory = parentCartegories[parentCartegories.length - 1];
    }
  }
  const parentId = parentCategory?.id;
  //BRAND TABS
  const designerCat =
    product.categories &&
    product.categories.filter(
      (item) => item.parentId === config.routes.brand.id
    )[0];
  const designerCatId = designerCat?.id;

  const hasRelated = product && product.relatedProducts?.length > 0;
 
  return (
    <ProductPageContainer>
      <ProductContainer>
        <KlaviyoViewedProduct product={product} />
        <CustomData product={product}>
          {(customFields, customTabs, customData) => (
            <>
              <Section className="product-images-wrapper">
                <ImageSlider
                  product={product}
                  customData={customData}
                  selectedVariation={variantImageSlider}
                />
                <Above breakpoint="lg">
                  {(matches) =>
                    matches && designerCatId ? (
                      <SmallProductRowWrapper>
                        <ProductRow
                          excludeId=''
                          id={designerCatId}
                          slider={true}
                          title={'samedesigner'}
                          subTitle=""
                          orderBy="BESTSELLER"
                        />
                      </SmallProductRowWrapper>
                    ) : (
                      <></>
                    )
                  }
                </Above>
              </Section>
              <Section className="product-info-wrapper">
                <ProductDetails
                  {...{
                    product,
                    selectedVariation,
                    variantHandler,
                    designerCat,
                    breadcrumbProps,
                  }}
                />
                <ProductTabsWrapper>
                  <ProductTabs
                    product={product}
                    customFields={customFields}
                    customTabs={customTabs}
                    loading={loading}
                  />
                </ProductTabsWrapper>
              </Section>
            </>
          )}
        </CustomData>
        <Below breakpoint="lg">
          {(matches) =>
            matches && designerCatId ? (
              <ProductRow
                excludeId=''
                id={designerCatId}
                slider={true}
                title={'samedesigner'}
                subTitle=""
                orderBy="BESTSELLER"
              />
            ) : (
              <></>
            )
          }
        </Below>
        {parentId && (
          <ProductRow
            excludeId={product.id}
            id={parentCategory.id}
            slider={true}
            title={bestSellingTitle}
            subTitle={bestSellingSubTitle}
            orderBy="BESTSELLER"
          />
        )}
        {product.recommendedProducts && (
          <>
            {product.recommendedProducts.shuffledToplist.length > 0 && (
              <Releware
                products={product.recommendedProducts.shuffledToplist}
                title={'Popular products'}
              />
            )}
            {product.recommendedProducts.viewed.length > 0 && (
              <Releware
                products={product.recommendedProducts.viewed}
                title={'Others also looked at'}
              />
            )}
            {product.recommendedProducts.bought.length > 0 && (
              <Releware
                products={product.recommendedProducts.bought}
                title={'Others also bought'}
              />
            )}
          </>
        )}
        {designerCat && <DesignerContent category={designerCat} />}

      </ProductContainer>
      <CategoriesContent categories={product.categories} title={blogTitle} />
    </ProductPageContainer>
  );
};

export default Product;
